import React, { useState, useEffect, useCallback } from 'react';

interface Alert {
  id: string;
  type: 'warning' | 'critical' | 'info';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  actionUrl?: string;
}

interface AlertConfig {
  wealthThreshold: number;
  unfilledOrderHours: number;
  botActivityMinimum: number;
  serverCpuThreshold: number;
  serverMemoryThreshold: number;
  enableWealthAlerts: boolean;
  enableOrderAlerts: boolean;
  enableBotAlerts: boolean;
  enableServerAlerts: boolean;
}

interface AdminAlertSystemProps {
  apiBaseUrl: string;
  adminKey: string;
}

export const AdminAlertSystem: React.FC<AdminAlertSystemProps> = ({ apiBaseUrl, adminKey }) => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [config, setConfig] = useState<AlertConfig>({
    wealthThreshold: 50,
    unfilledOrderHours: 24,
    botActivityMinimum: 10,
    serverCpuThreshold: 80,
    serverMemoryThreshold: 85,
    enableWealthAlerts: true,
    enableOrderAlerts: true,
    enableBotAlerts: true,
    enableServerAlerts: true,
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showConfig, setShowConfig] = useState<boolean>(false);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);

  const fetchAlerts = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await fetch(`${apiBaseUrl}/api/admin/prod/alerts`, {
        headers: { 'x-admin-key': adminKey },
      });
      if (response.ok) {
        const data = await response.json();
        setAlerts(Array.isArray(data.alerts) ? data.alerts : []);
      }
    } catch (err) {
      console.error('Error fetching alerts:', err);
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl, adminKey]);

  const saveConfig = useCallback(async (): Promise<void> => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/admin/prod/alert-config`, {
        method: 'POST',
        headers: {
          'x-admin-key': adminKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(config),
      });
      if (response.ok) {
        setShowConfig(false);
      }
    } catch (err) {
      console.error('Error saving alert config:', err);
    }
  }, [apiBaseUrl, adminKey, config]);

  const markAsRead = async (alertId: string): Promise<void> => {
    try {
      await fetch(`${apiBaseUrl}/api/admin/prod/alerts/${alertId}/read`, {
        method: 'POST',
        headers: { 'x-admin-key': adminKey },
      });
      setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, read: true } : a));
    } catch (err) {
      console.error('Error marking alert as read:', err);
    }
  };

  const dismissAlert = async (alertId: string): Promise<void> => {
    try {
      await fetch(`${apiBaseUrl}/api/admin/prod/alerts/${alertId}`, {
        method: 'DELETE',
        headers: { 'x-admin-key': adminKey },
      });
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch (err) {
      console.error('Error dismissing alert:', err);
    }
  };

  useEffect(() => {
    fetchAlerts();
    if (!autoRefresh) return;
    const interval = setInterval(() => fetchAlerts(), 30000);
    return () => clearInterval(interval);
  }, [fetchAlerts, autoRefresh]);

  const unreadCount = alerts.filter(a => !a.read).length;
  const criticalCount = alerts.filter(a => a.type === 'critical' && !a.read).length;

  const getAlertColor = (type: string): string => {
    switch (type) {
      case 'critical':
        return '#ef4444';
      case 'warning':
        return '#f59e0b';
      case 'info':
        return '#3b82f6';
      default:
        return '#a0aec0';
    }
  };

  const getAlertIcon = (type: string): string => {
    switch (type) {
      case 'critical':
        return '[!]';
      case 'warning':
        return '[!]';
      case 'info':
        return '[i]';
      default:
        return '[*]';
    }
  };

  const formatTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    if (diffSecs < 60) return `${diffSecs}s ago`;
    const diffMins = Math.floor(diffSecs / 60);
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div style={{
      backgroundColor: '#1a1a2e',
      border: '1px solid #4a5568',
      borderRadius: 0,
      padding: 16,
      marginBottom: 16,
      fontFamily: 'monospace',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h3 style={{ color: '#ffa500', fontSize: 14, margin: 0, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>🔔 ALERT SYSTEM</h3>
          {unreadCount > 0 && (
            <div style={{
              backgroundColor: criticalCount > 0 ? '#ef4444' : '#f59e0b',
              color: '#fff',
              padding: '2px 8px',
              borderRadius: 12,
              fontSize: 11,
              fontWeight: 600,
            }}>
              {unreadCount} {criticalCount > 0 ? `(${criticalCount} critical)` : ''}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#a0aec0', fontSize: 11, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            AUTO-REFRESH
          </label>
          <button
            onClick={() => setShowConfig(!showConfig)}
            style={{
              padding: '4px 8px',
              backgroundColor: '#8b5cf6',
              color: '#fff',
              border: 'none',
              fontSize: 11,
              cursor: 'pointer',
              borderRadius: 0,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              fontWeight: 600,
            }}
          >
            CONFIG
          </button>
          <button
            onClick={() => fetchAlerts()}
            disabled={isLoading}
            style={{
              padding: '4px 8px',
              backgroundColor: isLoading ? '#2d3748' : '#4a5568',
              color: isLoading ? '#718096' : '#e2e8f0',
              border: '1px solid #4a5568',
              fontSize: 11,
              cursor: isLoading ? 'default' : 'pointer',
              borderRadius: 2,
            }}
          >
            {isLoading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Alert Config */}
      {showConfig && (
        <div style={{
          backgroundColor: '#0b1220',
          border: '1px solid #4a5568',
          borderRadius: 0,
          padding: 12,
          marginBottom: 12,
        }}>
          <div style={{ color: '#ffa500', fontSize: 11, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>ALERT CONFIGURATION</div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 12,
            marginBottom: 12,
          }}>
            <div>
              <label style={{ color: '#a0aec0', fontSize: 10, display: 'block', marginBottom: 4 }}>
                Wealth Threshold (%): {config.wealthThreshold}
              </label>
              <input
                type="range"
                min="10"
                max="100"
                value={config.wealthThreshold}
                onChange={(e) => setConfig({ ...config, wealthThreshold: parseInt(e.target.value) })}
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <label style={{ color: '#a0aec0', fontSize: 10, display: 'block', marginBottom: 4 }}>
                Unfilled Order Hours: {config.unfilledOrderHours}
              </label>
              <input
                type="range"
                min="1"
                max="72"
                value={config.unfilledOrderHours}
                onChange={(e) => setConfig({ ...config, unfilledOrderHours: parseInt(e.target.value) })}
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <label style={{ color: '#a0aec0', fontSize: 10, display: 'block', marginBottom: 4 }}>
                Bot Activity Minimum: {config.botActivityMinimum}
              </label>
              <input
                type="range"
                min="1"
                max="100"
                value={config.botActivityMinimum}
                onChange={(e) => setConfig({ ...config, botActivityMinimum: parseInt(e.target.value) })}
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <label style={{ color: '#a0aec0', fontSize: 10, display: 'block', marginBottom: 4 }}>
                Server CPU Threshold (%): {config.serverCpuThreshold}
              </label>
              <input
                type="range"
                min="50"
                max="100"
                value={config.serverCpuThreshold}
                onChange={(e) => setConfig({ ...config, serverCpuThreshold: parseInt(e.target.value) })}
                style={{ width: '100%' }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            {[
              { key: 'enableWealthAlerts', label: 'Wealth Alerts' },
              { key: 'enableOrderAlerts', label: 'Order Alerts' },
              { key: 'enableBotAlerts', label: 'Bot Alerts' },
              { key: 'enableServerAlerts', label: 'Server Alerts' },
            ].map(({ key, label }) => (
              <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#a0aec0', fontSize: 11, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={config[key as keyof AlertConfig] as boolean}
                  onChange={(e) => setConfig({ ...config, [key]: e.target.checked })}
                  style={{ cursor: 'pointer' }}
                />
                {label}
              </label>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => saveConfig()}
              style={{
                flex: 1,
                padding: '6px 12px',
                backgroundColor: '#22c55e',
                color: '#fff',
                border: 'none',
                borderRadius: 2,
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Save Config
            </button>
            <button
              onClick={() => setShowConfig(false)}
              style={{
                flex: 1,
                padding: '6px 12px',
                backgroundColor: '#2d3748',
                color: '#e2e8f0',
                border: '1px solid #4a5568',
                borderRadius: 2,
                fontSize: 11,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Alerts List */}
      <div style={{
        backgroundColor: '#0b1220',
        border: '1px solid #4a5568',
        borderRadius: 0,
        maxHeight: 400,
        overflowY: 'auto',
      }}>
        {alerts.length === 0 ? (
          <div style={{
            padding: 16,
            color: '#718096',
            fontSize: 12,
            textAlign: 'center',
          }}>
            No alerts - system is healthy
          </div>
        ) : (
          alerts.map((alert, idx) => (
            <div
              key={alert.id || idx}
              style={{
                padding: 12,
                borderBottom: idx < alerts.length - 1 ? '1px solid #2d3748' : 'none',
                backgroundColor: alert.read ? '#0b1220' : '#1a1a2e',
                borderLeft: `3px solid ${getAlertColor(alert.type)}`,
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: 6,
              }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flex: 1 }}>
                  <span style={{ fontSize: 14 }}>{getAlertIcon(alert.type)}</span>
                  <div>
                    <div style={{
                      color: getAlertColor(alert.type),
                      fontSize: 11,
                      fontWeight: 600,
                      marginBottom: 2,
                    }}>
                      {alert.title}
                    </div>
                    <div style={{ color: '#e2e8f0', fontSize: 11, marginBottom: 4 }}>
                      {alert.message}
                    </div>
                    <div style={{ color: '#718096', fontSize: 10 }}>
                      {formatTime(alert.timestamp)}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {!alert.read && (
                    <button
                      onClick={() => markAsRead(alert.id)}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: '#3b82f6',
                        color: '#fff',
                        border: 'none',
                        fontSize: 10,
                        cursor: 'pointer',
                        borderRadius: 2,
                      }}
                    >
                      Mark Read
                    </button>
                  )}
                  <button
                    onClick={() => dismissAlert(alert.id)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: '#2d3748',
                      color: '#e2e8f0',
                      border: '1px solid #4a5568',
                      fontSize: 10,
                      cursor: 'pointer',
                      borderRadius: 2,
                    }}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
